import { useMemo } from 'react';
import sumBy from 'lodash/sumBy';

import { ProformaInvoiceFormGroupByMode } from '../../../../../teams/components/forms/ProformaInvoiceForm/ProformaInvoiceForm.types';

import {
  InvoiceItemsViewItemsType,
  useInvoiceItemsViewContext
} from '../../hooks/useInvoiceItemsViewContext';
import { useProformaInvoiceFormItemsViewDataTable } from '../../../../../teams/components/forms/ProformaInvoiceForm/components/ProformaInvoiceFormItemsView/hooks/useProformaInvoiceFormItemsViewDataTable';
import { useInvoiceItemsViewColumns } from '../../hooks/useInvoiceItemsViewColumns';

import { IndexTableThreeStatesCheckbox } from '../../../../../common/components/tables/IndexTableThreeStatesCheckbox';

import { DateHelper } from '../../../../../../helpers/DateHelper';
import { NextPureLinkHelper } from '../../../../../../helpers/links/NextPureLinkHelper';

import { TaskPath } from '../../../../../tasks/TaskPath';
import { ProjectPath } from '../../../../../projects/ProjectPath';
import { Translate } from '../../../../../../helpers/Translate';
import { MoneyHelper } from '../../../../../../helpers/MoneyHelper';

import { words } from '../../../../../../locales/keys';
import { Icon } from '../../../../../../helpers/Icon';
import { IconsEnum } from '../../../../../../assets/icons/types';
import { ClassName } from '../../../../../../types';

import { amountMultiply } from '../../../../../../utils/amountMultiply';

interface InvoiceItemsViewTableGroupRowsHeaderProps {
  className?: ClassName;
  items: InvoiceItemsViewItemsType;
  readonly?: boolean;
}

const headerDateFormat = 'MMMM yyyy';

function InvoiceItemsViewTableGroupRowsHeader({
  className,
  items,
  readonly
}: InvoiceItemsViewTableGroupRowsHeaderProps) {
  const { checkedHash, handleSetCheckedIds, watchFields, selectedCurrency } =
    useInvoiceItemsViewContext();

  const { checkboxState, handleCheckItems } =
    useProformaInvoiceFormItemsViewDataTable({
      items: items,
      checkedHash,
      onCheck: handleSetCheckedIds
    });

  const { showCheckboxCol } = useInvoiceItemsViewColumns({ readonly });

  const groupedId = items.map((item) => item.groupedId).join(',');

  const item = items?.[0];

  const itemsSubtotal = sumBy(items, (item) =>
    amountMultiply(item.convertedPrice, 1)
  );

  const headerGroup = useMemo(() => {
    switch (watchFields.watchGroupBy) {
      case ProformaInvoiceFormGroupByMode.TASK:
        return (
          <div className="flex justify-between">
            <NextPureLinkHelper
              className="hover:underline"
              href={TaskPath.show(item.taskNanoId)}
              text={item.taskName}
            />

            <div className="text-xs whitespace-nowrap pl-2 pr-1 text-right">
              <MoneyHelper
                currency={selectedCurrency}
                value={itemsSubtotal}
                withPrefixIfUsd
                withSuffix
              />
            </div>
          </div>
        );

      case ProformaInvoiceFormGroupByMode.PROJECT_TASK:
        return (
          <div className="flex justify-between">
            <div>
              <NextPureLinkHelper
                className="hover:underline"
                href={ProjectPath.show(
                  'rootProjectNanoId' in item
                    ? item?.rootProjectNanoId
                    : item?.projectNanoId
                )}
                text={
                  'rootProjectName' in item
                    ? item?.rootProjectName
                    : item?.projectName
                }
              />
              <Icon
                icon={IconsEnum.ARROW_NARROW_RIGHT_SOLID}
                className="h-4 w-4 inline mx-0.5"
              />
              <NextPureLinkHelper
                className="hover:underline"
                href={TaskPath.show(item.taskNanoId)}
                text={item.taskName}
              />
            </div>

            <div className="text-xs whitespace-nowrap pl-2 pr-1 text-right">
              <MoneyHelper
                currency={selectedCurrency}
                value={itemsSubtotal}
                withPrefixIfUsd
                withSuffix
              />
            </div>
          </div>
        );

      case ProformaInvoiceFormGroupByMode.PROJECT:
        return (
          <div className="flex justify-between">
            <NextPureLinkHelper
              className="hover:underline"
              href={ProjectPath.show(
                'rootProjectNanoId' in item
                  ? item?.rootProjectNanoId
                  : item?.projectNanoId
              )}
              text={
                'rootProjectName' in item
                  ? item?.rootProjectName
                  : item?.projectName
              }
            />

            <div className="text-xs whitespace-nowrap pl-2 pr-1 text-right">
              <MoneyHelper
                currency={selectedCurrency}
                value={itemsSubtotal}
                withPrefixIfUsd
                withSuffix
              />
            </div>
          </div>
        );

      case ProformaInvoiceFormGroupByMode.PROJECT_GROUP:
        return (
          <div className="flex justify-between">
            <div>
              {'rootProjectNanoId' in item &&
              item.rootProjectNanoId &&
              item.rootProjectNanoId !== item.projectNanoId ? (
                <>
                  <NextPureLinkHelper
                    className="hover:underline"
                    href={ProjectPath.show(item.rootProjectNanoId)}
                    text={item.rootProjectName}
                  />
                  <Icon
                    icon={IconsEnum.ARROW_NARROW_RIGHT_SOLID}
                    className="h-4 w-4 inline mx-0.5"
                  />
                </>
              ) : null}
              <NextPureLinkHelper
                className="hover:underline"
                href={ProjectPath.show(item.projectNanoId)}
                text={item.projectName}
              />
            </div>

            <div className="text-xs whitespace-nowrap pl-2 pr-1 text-right">
              <MoneyHelper
                currency={selectedCurrency}
                value={itemsSubtotal}
                withPrefixIfUsd
                withSuffix
              />
            </div>
          </div>
        );

      case ProformaInvoiceFormGroupByMode.PROJECT_GROUP_TASK:
        return (
          <div className="flex justify-between">
            <div>
              {'rootProjectNanoId' in item &&
              item.rootProjectNanoId &&
              item.rootProjectNanoId !== item.projectNanoId ? (
                <>
                  <NextPureLinkHelper
                    className="hover:underline"
                    href={ProjectPath.show(item.rootProjectNanoId)}
                    text={item.rootProjectName}
                  />
                  <Icon
                    icon={IconsEnum.ARROW_NARROW_RIGHT_SOLID}
                    className="h-4 w-4 inline mx-0.5"
                  />
                </>
              ) : null}
              <NextPureLinkHelper
                className="hover:underline"
                href={ProjectPath.show(item.projectNanoId)}
                text={item.projectName}
              />
              <Icon
                icon={IconsEnum.ARROW_NARROW_RIGHT_SOLID}
                className="h-4 w-4 inline mx-0.5"
              />
              <NextPureLinkHelper
                className="hover:underline"
                href={TaskPath.show(item.taskNanoId)}
                text={item.taskName}
              />
            </div>

            <div className="text-xs whitespace-nowrap pl-2 pr-1 text-right">
              <MoneyHelper
                currency={selectedCurrency}
                value={itemsSubtotal}
                withPrefixIfUsd
                withSuffix
              />
            </div>
          </div>
        );

      case ProformaInvoiceFormGroupByMode.GROUP_TASK:
        return (
          <div className="flex justify-between">
            <div>
              <NextPureLinkHelper
                className="hover:underline"
                href={ProjectPath.show(item.projectNanoId)}
                text={item.projectName}
              />
              <Icon
                icon={IconsEnum.ARROW_NARROW_RIGHT_SOLID}
                className="h-4 w-4 inline mx-0.5"
              />
              <NextPureLinkHelper
                className="hover:underline"
                href={TaskPath.show(item.taskNanoId)}
                text={item.taskName}
              />
            </div>

            <div className="text-xs whitespace-nowrap pl-2 pr-1 text-right">
              <MoneyHelper
                currency={selectedCurrency}
                value={itemsSubtotal}
                withPrefixIfUsd
                withSuffix
              />
            </div>
          </div>
        );

      case ProformaInvoiceFormGroupByMode.MONTH_INVOICE_CREATED:
        return (
          <DateHelper
            date={item.invoiceCreationMonth}
            customFormat={headerDateFormat}
          />
        );

      case ProformaInvoiceFormGroupByMode.MONTH_TASK_CREATED:
        return (
          <DateHelper
            date={item.taskCreatedAt}
            customFormat={headerDateFormat}
          />
        );

      case ProformaInvoiceFormGroupByMode.MONTH_TASK_DUE_DATE:
        return (
          <DateHelper
            date={item.taskDueDateMonth}
            customFormat={headerDateFormat}
          />
        );

      case ProformaInvoiceFormGroupByMode.MONTH_TASK_DONE:
        return item.taskDoneMonth ? (
          <DateHelper
            date={item.taskDoneMonth}
            customFormat={headerDateFormat}
          />
        ) : (
          <Translate id={words.na} />
        );

      case ProformaInvoiceFormGroupByMode.MONTH_PROJECT_CLOSED:
        return item.projectClosureMonth ? (
          <DateHelper
            date={item.projectClosureMonth}
            customFormat={headerDateFormat}
          />
        ) : (
          <Translate id={words.na} />
        );

      case ProformaInvoiceFormGroupByMode.MONTH_PROJECT_CREATED:
        return (
          <DateHelper
            date={item.projectCreatedAt}
            customFormat={headerDateFormat}
          />
        );

      case ProformaInvoiceFormGroupByMode.INVOICE_TAG:
        return (
          items[0]?.items?.[0]?.invoices?.[0]?.invoiceTag || (
            <Translate id={words.untagged} />
          )
        );

      default:
        return '';
    }
  }, [item, items, itemsSubtotal, selectedCurrency, watchFields.watchGroupBy]);

  return (
    <tr className={className || 'h-full'}>
      {/* Checkbox */}
      {showCheckboxCol && (
        <td className="text-xs p-0 h-full pt-2 tracking-tight">
          <div className="flex h-full items-center py-1 px-3 bg-gray-100 dark:bg-gray-800 sm:-ml-2 pl-3 rounded-l-md sm:-mr-2 pr-3 rounded-r-md">
            <IndexTableThreeStatesCheckbox
              checked={checkboxState}
              className="flex items-center justify-center"
              checkBoxClassName="basic-checkbox"
              itemId={groupedId}
              indeterminate={checkboxState === undefined}
              onCheck={handleCheckItems}
              scope="proforma-invoice-items"
            />
          </div>
        </td>
      )}

      <td colSpan={99} className="text-xs p-0 h-full pt-2 tracking-tight">
        <div className="h-full items-center py-1 px-3 bg-gray-100 dark:bg-gray-800 sm:-ml-2 pl-3 rounded-l-md sm:-mr-2 pr-3 rounded-r-md">
          {headerGroup}
        </div>
      </td>
    </tr>
  );
}

export default InvoiceItemsViewTableGroupRowsHeader;
